import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import BarChart from '@decisiv/iconix/lib/components/BarChart';
import ChessCastle from '@decisiv/iconix/lib/components/ChessCastle';
import Grid from '@decisiv/iconix/lib/components/Grid';

import OptionsList from '@decisiv/ui-components/lib/atoms/OptionsList';
import Popover from '@decisiv/ui-components/lib/components/Popover';

import Button from './styles';

// Badge per link kind, matching managed-care-ui's app switcher.
const DECORATIONS = {
  portal: {
    type: 'Badge',
    icon: ChessCastle,
    color: 'success',
    palette: 'pastel',
  },
  insite: {
    type: 'Badge',
    icon: BarChart,
    color: 'carrotCake',
    palette: 'pastel',
  },
};

/**
 * App switcher for the Management top nav.
 *
 * Links are built server-side by UserAppLinks (portal is the source of truth for
 * them), so this component only renders — unlike managed-care-ui's version,
 * which resolves them through GraphQL.
 */
function AppsLinks({ links }) {
  const [active, setActive] = useState(false);
  const popoverRef = useRef(null);

  const items = useMemo(
    () =>
      links.map((link) => ({
        id: link.id,
        label: link.label,
        decoration: DECORATIONS[link.kind],
      })),
    [links],
  );

  const toggleActive = () => setActive((wasActive) => !wasActive);

  const openLink = (id) => {
    if (popoverRef.current) popoverRef.current.hide();

    const link = links.find((candidate) => candidate.id === id);
    if (link) window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  if (!links.length) return null;

  return (
    <Popover
      ref={popoverRef}
      zIndex={10}
      onShow={toggleActive}
      onHide={toggleActive}
      showArrow={false}
      target={
        <Button type="button" aria-label="Show Apps links" $active={active}>
          <Grid />
        </Button>
      }
    >
      <OptionsList items={items} onChange={openLink} />
    </Popover>
  );
}

AppsLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string,
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
};

AppsLinks.defaultProps = {
  links: [],
};

export default AppsLinks;
